import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { getShortlistByPlaylistId } from '@disco/state-management/selectors/shortlists'

import type { PlaylistId } from 'types/playlist'

import { getShortlistBaseRoute } from 'utils/routing'

import { useSelector } from 'hooks/use-redux'

/**
 * Returns the callback "Playlist Settings" uses to say it needs a shortlist
 * that does not exist yet, and navigates to the save modal once one appears.
 *
 * This has to live in a component that outlives the menu: menus unmount on
 * close, so an effect inside PlaylistSettings itself would never fire.
 */
export const usePlaylistSettingsAwait = (
  playlist: { playlist_id: PlaylistId } | null | undefined,
): (() => void) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [pending, setPending] = React.useState(false)
  const shortlist = useSelector(state =>
    playlist ? getShortlistByPlaylistId(state, playlist.playlist_id) : null,
  )

  React.useEffect(() => {
    if (!pending || !shortlist || !shortlist.items?.length) return
    const baseRoute = getShortlistBaseRoute(location)
    navigate(`${baseRoute}/shortlists/${shortlist.id}/save`, {
      state: { location: 'playlist_settings' },
    })
    setPending(false)
  }, [pending, shortlist, navigate, location])

  return React.useCallback(() => setPending(true), [])
}
